import { useState, useEffect } from 'react';
import { CheckCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ValidateDialog({ order, open, onClose, onDone }) {
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) setQty(Object.fromEntries(order.items.map(oi => [oi.item_id, String(oi.adjusted_quantity ?? oi.quantity)])));
  }, [order]);

  if (!order) return null;
  const finalQty = (oi) => Math.max(0, parseInt(qty[oi.item_id] ?? oi.quantity) || 0);
  const total = order.items.reduce((s, oi) => s + oi.unit_price * finalQty(oi), 0);
  const reduced = order.items.some(oi => finalQty(oi) !== oi.quantity);

  const submit = async () => {
    if (total === 0 && !window.confirm("Toutes les quantités sont à 0. Confirmer quand même ?")) return;
    setSaving(true);
    try {
      const items = order.items.map(oi => ({ item_id: oi.item_id, adjusted_quantity: finalQty(oi) }));
      const r = await api.put(`/admin/preorders/${order.id}/validate`, { items });
      toast.success(r.data.email_sent ? "Commande validée, email de confirmation envoyé" : "Commande validée (email non envoyé : adresse invalide ou envoi impossible)");
      onDone?.(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la validation"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="validate-dialog">
        <DialogHeader>
          <DialogTitle>Valider la commande de {order.first_name} {order.last_name}</DialogTitle>
          <DialogDescription>Ajustez les quantités accordées si besoin : le client recevra un email de confirmation avec les quantités finales.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {order.items.map(oi => (
            <div key={oi.item_id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`validate-item-${oi.item_id}`}>
              <div className="min-w-0">
                <p className="font-medium text-sm text-slate-900 truncate">{oi.item_name}</p>
                <p className="text-xs text-slate-500">Demandé : {oi.quantity} · {oi.unit_price.toFixed(2)} € l'unité</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500">Accordé</span>
                <Input type="number" min="0" max={oi.quantity} value={qty[oi.item_id] ?? ''} onChange={e => setQty(q => ({ ...q, [oi.item_id]: e.target.value }))} className="w-20 h-9 rounded-lg text-center font-semibold" data-testid={`validate-qty-${oi.item_id}`} />
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 text-sm">
            <span className="text-slate-500">{reduced ? 'Quantités réduites : le client en sera informé dans l\'email' : 'Quantités demandées accordées en totalité'}</span>
            <span className="font-bold text-violet-600 text-base" data-testid="validate-total">{total.toFixed(2)} €</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="validate-cancel-btn">Annuler</Button>
          <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={saving} data-testid="validate-confirm-btn">
            {saving ? <Mail className="h-4 w-4 mr-2 animate-pulse" /> : <CheckCircle className="h-4 w-4 mr-2" />} Valider et envoyer l'email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
